/**
 * @fileoverview React contexts for renderer-wide injectables.
 * @module inspection-renderer/contracts
 *
 * Responsibilities:
 * - Provide React `Context` carriers for the host-injected
 *   `AttachmentPicker` and `ResolveContext` so the 18 widgets do NOT have
 *   to thread the props through every dispatcher seam.
 * - Provide `useAttachmentPicker()` and `useResolveContext()` hooks that
 *   read the active context. Hooks return `null` when the host hasn't
 *   wired the provider (B13 — widgets must tolerate absent pickers
 *   gracefully so tests can mount them without provider setup).
 *
 * Dependencies:
 * - React 18 (peer).
 *
 * Architecture:
 * - Pure React Context. No state, no effects.
 *
 * @hook useAttachmentPicker
 * Purpose: read the host-injected attachment-picker (Camera/Photo/Signature).
 * Returns: AttachmentPicker | null
 * Side Effects: none.
 * Offline Behavior: host's picker is responsible for offline routing (D-0.20).
 *
 * @hook useResolveContext
 * Purpose: read the host-injected data-bound resolver.
 * Returns: ResolveContext | null
 * Side Effects: none.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { AttachmentPicker } from './attachmentPicker';
import type { ResolveContext } from './resolveContext';
import type { InspectorRosterEntry } from '../inspectors/inspectorSelection';

/**
 * @contract ThumbnailResolver
 * Purpose: host-injected callback to resolve an attachment ID to a displayable
 * thumbnail URL. Mobile-pwa resolves from IndexedDB; tenant-web resolves via
 * SAS URL. Returns null if the thumbnail is not available.
 */
export type ThumbnailResolver = (attachmentId: string) => Promise<string | null>;

const AttachmentPickerContext = createContext<AttachmentPicker | null>(null);
const ResolveContextCtx = createContext<ResolveContext | null>(null);
const OfflineContext = createContext<boolean>(false);
const InspectorRosterContext = createContext<readonly InspectorRosterEntry[]>([]);
const ThumbnailResolverContext = createContext<ThumbnailResolver | null>(null);

export function useAttachmentPicker(): AttachmentPicker | null {
  return useContext(AttachmentPickerContext);
}

export function useResolveContext(): ResolveContext | null {
  return useContext(ResolveContextCtx);
}

/** @hook useIsOffline — read host-injected offline flag (defaults to false). */
export function useIsOffline(): boolean {
  return useContext(OfflineContext);
}

/** @hook useInspectorRoster — read host-injected event inspector roster. */
export function useInspectorRoster(): readonly InspectorRosterEntry[] {
  return useContext(InspectorRosterContext);
}

/** @hook useThumbnailResolver — read host-injected thumbnail resolver (defaults to null). */
export function useThumbnailResolver(): ThumbnailResolver | null {
  return useContext(ThumbnailResolverContext);
}

/**
 * @contract RendererProvidersProps
 * Purpose: prop contract for the single context-injection wrap.
 */
export interface RendererProvidersProps {
  attachmentPicker?: AttachmentPicker;
  resolveContext?: ResolveContext;
  /** Whether the host is currently offline (drives offline pill on widgets). */
  isOffline?: boolean;
  /** Event inspector roster used by the `inspectors` widget. */
  inspectorRoster?: readonly InspectorRosterEntry[];
  /** Host-injected thumbnail resolver for visual photo previews. */
  thumbnailResolver?: ThumbnailResolver;
  children: ReactNode;
}

/**
 * @component RendererProviders
 * Purpose: single wrap that injects all renderer-wide contexts. Hosts mount
 * this once at the top of the `<InspectionRenderer>` tree.
 *
 * Props:
 * - attachmentPicker: optional. When omitted, Camera/Photo/Signature
 *   widgets render their capture buttons as disabled (no host wired).
 * - resolveContext: optional. When omitted, DataBound widget falls back
 *   to its `extras.placeholder`.
 *
 * Behavior: pure context provider.
 * Data Source: n/a.
 * Constraints: no API calls; no Dexie writes.
 * Performance: passes references through; consumers re-render only when
 *   the context value identity changes.
 */
export function RendererProviders(props: RendererProvidersProps): ReactNode {
  const { attachmentPicker, resolveContext, isOffline, inspectorRoster, thumbnailResolver, children } = props;
  return (
    <AttachmentPickerContext.Provider value={attachmentPicker ?? null}>
      <ResolveContextCtx.Provider value={resolveContext ?? null}>
        <OfflineContext.Provider value={!!isOffline}>
          <InspectorRosterContext.Provider value={inspectorRoster ?? []}>
            <ThumbnailResolverContext.Provider value={thumbnailResolver ?? null}>
              {children}
            </ThumbnailResolverContext.Provider>
          </InspectorRosterContext.Provider>
        </OfflineContext.Provider>
      </ResolveContextCtx.Provider>
    </AttachmentPickerContext.Provider>
  );
}
